import { useContext, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AppContext } from "../../App";
import PopUp from "../PopUp";
import LoadIcon from "../../icons/LoadIcon";
import api from "../api/api";
import functions from "../../functions/functions";

const toCents = (v) => Math.round(Number(v || 0) * 100);

function VoucherlyPopup({ order, edit }) {
	const {
		setVoucherlyPopup,
		init_cart,
		apiSettings,
		setConfirm,
		setLoading,
		openOrder,
		setPaymentPopup,
		refreshOrdersState,
		loadNextSplittedOrder,
	} = useContext(AppContext);

	const [status, setStatus] = useState("loading");
	const [qrValue, setQrValue] = useState(null);
	// Pagamento parziale (completionMode Partial): quota incassata da Voucherly e residuo da incassare in cassa
	const [partial, setPartial] = useState(null);
	const partialOrderRef = useRef(null);
	const timerRef = useRef(null);

	useEffect(() => {
		if (order) initPayment();
		return clearPollTimer;
	}, [order]);

	useEffect(() => {
		if (status === "confirmed") {
			init_cart();
			loadNextSplittedOrder();
			setVoucherlyPopup(false);
			window.parent.postMessage("close", "*");
		}
	}, [status]);

	// Quota da chiedere a Voucherly: la riga Voucherly del carrello (non ancora incassata),
	// altrimenti quanto manca al totale dell'ordine
	const getVoucherlyAmount = () => {
		const payments = order.payments || [];
		const requested = payments
			.filter((p) => p.payment_method_id == 21 && !p.gateway)
			.reduce((s, p) => s + toCents(p.amount), 0);
		if (requested > 0) return (requested / 100).toFixed(2);
		const paid = payments.reduce((s, p) => s + toCents(p.amount) - toCents(p.rest), 0);
		return (Math.max(0, toCents(order.total) - paid) / 100).toFixed(2);
	};

	const initPayment = async () => {
		clearPollTimer();
		try {
			const params = new URLSearchParams({
				mandant_id: apiSettings.mandant.id,
				order_id: order.id,
				shop_id: order.shop_id,
				amount: getVoucherlyAmount(),
				origin: "pos",
			});
			const json = await api.get("/plugins/voucherly/init_payment.php?" + params.toString());
			if (json.success === 0) {
				setStatus("error");
			} else {
				setQrValue(json.data.checkout_url);
				setStatus("qr");
				timerRef.current = setTimeout(pollOrderStatus, 5000);
			}
		} catch (e) {
			setStatus("error");
		}
	};

	const pollOrderStatus = async () => {
		const res = await api.postProtected("/orders/get/" + order.id + "/", {
			fields: ["status"],
		});
		if (res.success === 0) {
			setStatus("error");
		} else {
			const orderStatus = res.data.status;
			if (orderStatus === "confirmed" || orderStatus === "payment_error") {
				setStatus(orderStatus);
				return;
			}
			// Voucherly ha chiuso il pagamento ma non copre il totale: righe registrate dal callback
			// con il payment_id del pagamento in corso, ordine ancora in attesa di pagamento
			const payments = res.data.payments || [];
			const voucherlyPaid = payments
				.filter((p) => p.gateway === "voucherly" && res.data.payment_id && p.payment_id === res.data.payment_id)
				.reduce((s, p) => s + toCents(p.amount), 0);
			if (voucherlyPaid > 0) {
				const paid = payments.reduce((s, p) => s + toCents(p.amount) - toCents(p.rest), 0);
				partialOrderRef.current = res.data;
				setPartial({
					paid: voucherlyPaid / 100,
					remaining: Math.max(0, toCents(res.data.total) - paid) / 100,
				});
				setStatus("partial");
			} else {
				timerRef.current = setTimeout(pollOrderStatus, 5000);
			}
		}
	};

	const clearPollTimer = () => {
		if (!timerRef.current) return;
		clearTimeout(timerRef.current);
		timerRef.current = null;
	};

	const retry = async () => {
		clearPollTimer();
		setStatus("loading");
		setQrValue(null);
		const res = await api.postProtected("/orders/edit/" + order.id + "/", {
			status: "awaiting_payment",
		});
		if (res.success === 0) {
			setStatus("error");
		} else {
			await initPayment();
		}
	};

	// Apre l'ordine nel carrello con le quote Voucherly già registrate e il popup pagamenti sul residuo
	const payRemaining = async () => {
		setVoucherlyPopup(false);
		await openOrder(partialOrderRef.current);
		setPaymentPopup(true);
	};

	// Chiude senza incassare il residuo: l'ordine resta in attesa di pagamento e si riprende dalla lista ordini
	const closePartial = () => {
		setVoucherlyPopup(false);
		init_cart();
		refreshOrdersState();
	};

	const cancel = () => {
		clearPollTimer();
		setConfirm({
			title: "Attenzione",
			message: "Sei sicuro di annullare il pagamento Voucherly?",
			onConfirm: async () => {
				if (!edit) {
					setLoading(true);
					const res = await api.postProtected("/orders/edit/" + order.id + "/", {
						status: "archived",
					});
					setLoading(false);
					if (res.success === 1) {
						setVoucherlyPopup(false);
					} else {
						setConfirm({ title: "Errore", message: res.error });
					}
				} else {
					setVoucherlyPopup(false);
				}
			},
		});
	};

	return (
		<PopUp
			customClass="satispay-payment"
			size=""
			options={{
				title: "Pagamento Voucherly",
				onClose: false,
			}}
		>
			{status === "loading" && (
				<div className="satispay-inner">
					<LoadIcon className="rotate" />
					<p>Inizializzazione pagamento...</p>
				</div>
			)}

			{status === "qr" && (
				<div className="satispay-inner">
					<div className="satispay-qr-wrapper">
						<a href={qrValue} target="_blank" rel="noreferrer" title="Apri pagamento">
							<QRCodeSVG value={qrValue} size={240} />
						</a>
					</div>
					<p className="satispay-instruction">Scansiona per pagare</p>
					<LoadIcon className="rotate" />
					<button onClick={cancel} className="button-cancel satispay-cancel-button">
						Annulla
					</button>
				</div>
			)}

			{status === "partial" && partial && (
				<div className="satispay-inner">
					<p>Pagato con Voucherly: {functions.formatter.format(partial.paid)}</p>
					<p className="satispay-instruction">
						Mancano {functions.formatter.format(partial.remaining)}
					</p>
					<div className="satispay-actions">
						<button onClick={payRemaining} className="button-confirm">Incassa il resto</button>
						<button onClick={closePartial} className="button-cancel">Chiudi</button>
					</div>
				</div>
			)}

			{status === "payment_error" && (
				<div className="satispay-inner satispay-error">
					<p>Pagamento non riuscito</p>
					<div className="satispay-actions">
						<button onClick={retry} className="button-confirm">Riprova</button>
						<button onClick={cancel} className="button-cancel">Annulla</button>
					</div>
				</div>
			)}

			{status === "error" && (
				<div className="satispay-inner satispay-error">
					<p>Errore inizializzazione pagamento</p>
					<div className="satispay-actions">
						<button onClick={retry} className="button-confirm">Riprova</button>
						<button onClick={cancel} className="button-cancel">Annulla</button>
					</div>
				</div>
			)}
		</PopUp>
	);
}

export default VoucherlyPopup;
